import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Badge, Tag, Row, Col } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { permissionsFromRoles } from '../../../../core/utils/dataManipulating/organization';

const DraggableOrgNode = ({ org, isSelected, onSelect, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isCurrentlyDragging,
  } = useDraggable({
    id: org.id,
    data: {
      type: 'org',
      org,
    },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    padding: '8px 12px',
    margin: '2px 0',
    border: isSelected ? '2px solid #1890ff' : '1px solid #e8e8e8',
    borderRadius: '6px',
    backgroundColor: isSelected ? '#e6f7ff' : '#fff',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    minHeight: '40px',
    userSelect: 'none',
  };

  const handleClick = (e) => {
    // Don't trigger select when clicking drag handle
    if (e.target.closest('.drag-handle')) {
      return;
    }
    onSelect(org);
  };

  // Check if org is enabled based on roles
  const isEnabled = Object.keys(
    permissionsFromRoles([org.roles.find(role => role.isMaster)])
  ).includes('ENABLED');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`draggable-org-node ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
      onClick={handleClick}
    >
      <Row align="middle" style={{ width: '100%' }}>
        {/* Drag Handle */}
        <Col flex="none">
          <div
            className="drag-handle"
            {...attributes}
            {...listeners}
            style={{
              cursor: 'grab',
              padding: '4px',
              marginRight: '8px',
              color: '#999',
              borderRadius: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f0f0';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            <HolderOutlined style={{ fontSize: '12px' }} />
          </div>
        </Col>

        {/* Org Status Badge */}
        <Col flex="none">
          <Badge
            dot
            style={{
              backgroundColor: isEnabled ? '#52c41a' : '#d9d9d9'
            }}
          />
        </Col>

        {/* Org Name */}
        <Col flex="auto">
          <span 
            style={{ 
              marginLeft: '8px', 
              fontWeight: isSelected ? 'bold' : 'normal',
              color: isSelected ? '#1890ff' : '#000'
            }}
          >
            {org.name}
          </span>
        </Col>

        {/* Selected Tag */}
        {isSelected && (
          <Col flex="none">
            <Tag color="#87d068" style={{ marginLeft: '8px' }}>
              Selected
            </Tag>
          </Col>
        )}
      </Row>

      {/* Drag State Overlay */}
      {isDragging && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            border: '2px dashed #1890ff',
            borderRadius: '6px',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

export default DraggableOrgNode;
